import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { Dialog } from '@app/components/Shared/Dialog';
import {
  categorySelector,
  locationsTreeSelector,
  searchConditionState,
} from '@app/domain/catalog';
import { ProductLocation } from '@app/types/catalog';
import { getSearchResultUrl } from '@app/utils/catalog';

export interface ConditionProps {
  locationIds?: string[];
}
interface LocationsModalProps {
  category?: string;
  condition?: ConditionProps;
  onClickSearch?: (conditions: ConditionProps) => void;
  onClose: () => void;
  open: boolean;
}

export function LocationsModal({
  category,
  condition,
  onClickSearch,
  onClose,
  open,
}: LocationsModalProps): ReactElement {
  const navigate = useNavigate();
  const setConditionState = useSetRecoilState(searchConditionState);
  const categories = useRecoilValue(categorySelector);
  const locations = useRecoilValue(locationsTreeSelector);
  const [conditions, setConditions] = useState<ConditionProps>({
    locationIds: condition?.locationIds || [],
  });

  const isGroupChecked = (id: string) => {
    return !!locations
      ?.find((i) => i.id === id)
      ?.children?.every((c) => conditions.locationIds?.includes(c.id));
  };

  const isIndeterminate = (id: string) => {
    const children = locations?.find((i) => i.id === id)?.children;
    return !!(
      children?.some((c) => conditions.locationIds?.includes(c.id)) &&
      !children?.every((c) => conditions.locationIds?.includes(c.id))
    );
  };

  const handleChangeChecked = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, id?: string) => {
      event.stopPropagation();
      const targetIds: string[] = [];
      if (id) {
        locations
          ?.find((c) => c.id === id)
          ?.children?.forEach((c) => targetIds.push(c.id));
      } else {
        targetIds.push(event.target.value);
      }
      const data = conditions.locationIds || [];
      setConditions({
        ...conditions,
        locationIds: event.target.checked
          ? [...data, ...targetIds]
          : data.filter((c) => !targetIds.includes(c)),
      });
    },
    [conditions, locations]
  );

  const categoryId = useMemo(() => {
    return categories?.find((c) => c.name === category)?.id || '';
  }, [categories, category]);

  const handleClose = useCallback((): void => {
    onClose();
  }, [onClose]);

  const handleClickSearch = useCallback(() => {
    if (onClickSearch) {
      onClickSearch(conditions);
      return;
    }
    setConditionState(null);
    navigate(
      getSearchResultUrl({
        categoryId,
        locationIds: conditions.locationIds,
      })
    );
    handleClose();
  }, [
    categoryId,
    conditions,
    handleClose,
    navigate,
    onClickSearch,
    setConditionState,
  ]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="エリアから探す"
      content={
        <Box>
          {!!locations.length &&
            locations.map((region: ProductLocation) => (
              <Accordion
                key={region.id}
                TransitionProps={{ unmountOnExit: true }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" alignItems="center">
                    <Checkbox
                      value={region.id}
                      checked={isGroupChecked(region.id)}
                      indeterminate={isIndeterminate(region.id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => handleChangeChecked(e, region.id)}
                    />
                    <Typography>{region.name}</Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 5, py: 1 }}>
                  <Stack>
                    {region.children &&
                      region.children.map((prefecture) => (
                        <FormControlLabel
                          key={prefecture.id}
                          label={prefecture.name}
                          control={
                            <Checkbox
                              value={prefecture.id}
                              checked={conditions.locationIds?.includes(
                                prefecture.id
                              )}
                              onChange={(e) => handleChangeChecked(e)}
                            />
                          }
                        />
                      ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
        </Box>
      }
      action={
        <Stack direction="row" spacing={2} width={1}>
          <Button
            color="inherit"
            variant="outlined"
            fullWidth
            onClick={handleClose}
          >
            キャンセル
          </Button>
          <Button
            color="primary"
            type="submit"
            variant="contained"
            fullWidth
            onClick={handleClickSearch}
          >
            検索
          </Button>
        </Stack>
      }
    />
  );
}
